import { plural } from '@lingui/core/macro';
import { Plural, Trans, useLingui } from '@lingui/react/macro';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import Banner from '../../components/Banner';
import Chip from '../../components/Chip';
import DataTable, { type DataTableColumn, type RowKey } from '../../components/DataTable';
import EmptyState from '../../components/EmptyState';
import Icon from '../../components/Icon';
import { PageSpinner } from '../../components/Spinner';
import { useToast } from '../../components/Toast';
import { errorMessage, fetchBomOverview, fetchCmdbCards } from '../../lib/api';
import { readOnlyNotice } from '../../lib/permissions';
import type { BomSaveResponse, BomVersion, CmdbCardRow } from '../../lib/types';
import BomEditor from './BomEditor';
import CveFindings from './CveFindings';
import StammIcon from './StammIcon';
import {
  bomGroups, bomRows, filterBomGroups, kevCount, kindLabel, rowsWithMatchingComponents, type BomGroup, type BomRow,
} from './bomModel';
import { groupProducts, productVersionLabel } from './productCatalogModel';

interface Props {
  canWrite: boolean;
  onCountsChange: () => Promise<void>;
}

/** Stückliste per product version: overview with findings and the editor (DEV-114). */
export default function BomOverview({ canWrite, onCountsChange }: Props): ReactNode {
  const toast = useToast();
  const { t, i18n } = useLingui();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState<{ products: CmdbCardRow[]; versions: CmdbCardRow[]; bom: BomVersion[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [kevOnly, setKevOnly] = useState(false);
  const [expanded, setExpanded] = useState<ReadonlySet<RowKey>>(new Set());
  // Versions opened inside an expanded product (keys of `BomRow`).
  const [openVersions, setOpenVersions] = useState<ReadonlySet<string>>(new Set());
  const [editing, setEditing] = useState<string | null>(null);
  const loadSeq = useRef(0);
  const focusedRef = useRef<string | null>(null);
  const containerRef = useRef<HTMLElement>(null);

  const load = useCallback(async (): Promise<void> => {
    const seq = ++loadSeq.current;
    setLoading(true);
    try {
      const [products, versions, bom] = await Promise.all([
        fetchCmdbCards('ProduktType'), fetchCmdbCards('ProduktVersion'), fetchBomOverview(),
      ]);
      if (seq !== loadSeq.current) return;
      setData({ products: products.rows, versions: versions.rows, bom: bom.versions });
      setError(null);
    } catch (caught) {
      if (seq === loadSeq.current) setError(errorMessage(caught));
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    return () => { ++loadSeq.current; };
  }, [load]);

  const rows = useMemo(
    // Fallback product names are translated, so the rows follow the UI language.
    () => bomRows(groupProducts(data?.products ?? [], data?.versions ?? []), data?.bom ?? []), [data, i18n.locale]);
  const groups = useMemo(() => bomGroups(rows), [rows]);
  const visibleGroups = useMemo(() => filterBomGroups(groups, search, kevOnly), [groups, search, kevOnly]);
  const visibleVersions = visibleGroups.reduce((sum, group) => sum + group.rows.length, 0);

  // `?version=<id>` from the product catalog, `?id=<entry>` from a link to one Stücklisteneintrag.
  const focusVersion = Number(params.get('version')) || null;
  const focusEntry = Number(params.get('id')) || null;
  const focusRow = rows.find((row) => (focusVersion !== null && row.version.id === focusVersion)
    || (focusEntry !== null && row.components.some((c) => c.entry_id === focusEntry)));
  const focusKey = focusRow ? groups.find((group) => group.rows.includes(focusRow))?.key : undefined;
  const focusVersionKey = focusRow?.key;
  const focusToken = focusVersion !== null ? `version:${focusVersion}` : focusEntry !== null ? `entry:${focusEntry}` : null;

  useEffect(() => {
    if (!focusKey || !focusToken) { focusedRef.current = null; return; }
    if (focusedRef.current === focusToken) return;
    focusedRef.current = focusToken;
    setSearch('');
    setKevOnly(false);
    setExpanded((current) => new Set([...current, focusKey]));
    if (focusVersionKey) setOpenVersions((current) => new Set([...current, focusVersionKey]));
    const frame = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        (containerRef.current?.querySelector('.bom-version.is-focused') ?? containerRef.current?.querySelector('.stamm-focus'))?.scrollIntoView({ block: 'nearest', behavior: 'auto' });
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [focusKey, focusToken, focusVersionKey]);

  const clearFocus = (): void => {
    focusedRef.current = null;
    if (!params.has('id') && !params.has('version')) return;
    setParams((current) => {
      const next = new URLSearchParams(current);
      next.delete('id');
      next.delete('version');
      return next;
    }, { replace: true });
  };

  const changeSearch = (value: string): void => {
    clearFocus();
    setSearch(value);
    // A hit opens its product, a component hit also its version.
    const hits = value.trim() ? filterBomGroups(groups, value, kevOnly).map((group) => group.key) : [];
    setExpanded(new Set(hits));
    setOpenVersions(new Set(rowsWithMatchingComponents(rows, value)));
  };

  const onSaved = async (result: BomSaveResponse): Promise<void> => {
    await load();
    await onCountsChange();
    if (result.errors.length === 0) {
      setEditing(null);
      const changed = result.added.length + result.removed.length;
      toast.success(changed
        ? t`Stückliste gespeichert (${plural(changed, { one: '# Änderung', other: '# Änderungen' })}).`
        : t`Stückliste unverändert.`);
    }
  };

  const editRow = editing ? rows.find((row) => row.key === editing) : undefined;

  const toggleVersion = (key: string): void => {
    clearFocus();
    setOpenVersions((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const editButton = (row: BomRow): ReactNode => {
    const { productName } = row;
    const versionLabel = productVersionLabel(row.version);
    return <button type="button" className="stamm-action-button" disabled={!canWrite}
      aria-label={t`Stückliste von ${productName} ${versionLabel} bearbeiten`}
      title={canWrite ? t`Stückliste bearbeiten` : readOnlyNotice('cmdb.write')}
      onClick={(event) => { event.stopPropagation(); setEditing(row.key); }}><Icon name="edit" size={18} /></button>;
  };

  const componentTable = (row: BomRow): ReactNode => {
    const { productName } = row;
    const versionLabel = productVersionLabel(row.version);
    return row.components.length === 0 ? (
    <EmptyState compact title={t`Noch keine Komponenten`}
      action={canWrite ? <button type="button" onClick={() => setEditing(row.key)}><Icon name="edit" /><Trans>Stückliste bearbeiten</Trans></button> : undefined}>
      <Trans>Ordnen Sie Komponenten zu oder laden Sie eine SBOM hoch.</Trans>
    </EmptyState>
  ) : (
    // A grid list, not a nested <table>: the outer data table's cell rules would apply to it.
    <div className="bom-components" role="table" aria-label={t`Stückliste ${productName} ${versionLabel}`}>
      <div className="bom-components-row bom-components-head" role="row">
        <span role="columnheader"><Trans>Komponente</Trans></span><span role="columnheader"><Trans>Version</Trans></span>
        <span role="columnheader"><Trans>Typ</Trans></span><span role="columnheader"><Trans>CVE-Befunde</Trans></span>
      </div>
      {row.components.map((component) => (
        <div key={component.entry_id ?? component.component_version_id} role="row"
          className={`bom-components-row${focusEntry !== null && component.entry_id === focusEntry ? ' is-focused' : ''}`}>
          <span role="rowheader" className="bom-component-name">
            <Link to={`/stammdaten?klasse=KomponenteVersion&id=${component.component_version_id}`}>{component.name}</Link>
            {component.manufacturer ? <span className="muted">{component.manufacturer}</span> : null}
          </span>
          <span role="cell" className="mono"><span className="bom-cell-label" aria-hidden="true"><Trans>Version</Trans> </span>{component.version || '—'}</span>
          <span role="cell"><span className="bom-cell-label" aria-hidden="true"><Trans>Typ</Trans> </span>
            {component.part ? kindLabel(component.part) : <span className="muted">—</span>}</span>
          <span role="cell"><CveFindings kevCves={component.kev_cves} assessments={component.assessments} /></span>
        </div>
      ))}
    </div>
  );
  };

  const versionBlock = (row: BomRow): ReactNode => {
    const open = openVersions.has(row.key);
    const kev = kevCount(row.components);
    const bodyId = `bom-version-${row.version.id}`;
    return (
      <article key={row.key} className={`component-version bom-version${row.custom ? ' product-custom-version bom-custom-version' : ''}${row.key === focusVersionKey ? ' is-focused' : ''}`}
        aria-label={`${row.productName} ${productVersionLabel(row.version)}`}>
        <div className="component-version-head">
          <button type="button" className="bom-version-toggle" aria-expanded={open} aria-controls={bodyId}
            onClick={() => toggleVersion(row.key)}>
            <Icon name={open ? 'chevron-down' : 'chevron-right'} size={16} />
            <StammIcon name="layers" /><strong>{productVersionLabel(row.version)}</strong>
            {row.custom ? <Chip tone="info"><Trans>Kundenversion</Trans></Chip> : null}
            <span className="bom-version-meta">
              <Plural value={row.components.length} one="# Komponente" other="# Komponenten" />
              {' · '}<Plural value={row.instanceCount} one="# Instanz" other="# Instanzen" />
            </span>
            {kev ? <Chip tone="alert"><Plural value={kev} one="# KEV-Treffer" other="# KEV-Treffer" /></Chip> : null}
          </button>
          <div className="stamm-actions">{editButton(row)}</div>
        </div>
        {open ? <div id={bodyId} className="bom-version-body">{componentTable(row)}</div> : null}
      </article>
    );
  };

  const columns: readonly DataTableColumn<BomGroup>[] = [
    { key: 'product', header: t`Produkt`, width: '40%', sortValue: (group) => group.productName,
      render: (group) => <div className="component-identity">
        <span className="stamm-record-icon"><StammIcon name="box" /></span>
        <div className="component-name"><strong>{group.productName}</strong>
          {group.productId === null ? <span className="component-unassigned"><Trans>Ohne Produktzuordnung</Trans></span> : null}
        </div>
      </div>,
    },
    { key: 'versions', header: t`Versionen`, sortValue: (group) => group.rows.length,
      render: (group) => <Plural value={group.rows.length} one="# Version" other="# Versionen" /> },
    { key: 'kev', header: t`KEV-Treffer`, sortValue: (group) => group.rows.filter((row) => kevCount(row.components)).length,
      render: (group) => {
        const affected = group.rows.filter((row) => kevCount(row.components) > 0).length;
        return affected ? <Chip tone="alert"><Plural value={affected} one="# Version" other="# Versionen" /></Chip> : <span className="muted"><Trans>Keine</Trans></span>;
      } },
    { key: 'instances', header: t`Instanzen`, sortValue: (group) => group.rows.reduce((sum, row) => sum + row.instanceCount, 0),
      render: (group) => <Plural value={group.rows.reduce((sum, row) => sum + row.instanceCount, 0)} one="# Instanz" other="# Instanzen" /> },
  ];

  const renderDetail = (group: BomGroup): ReactNode => (
    <div className="component-expanded">
      <div className="component-version-list">{group.rows.map(versionBlock)}</div>
    </div>
  );

  return (
    <section ref={containerRef} className="card stamm-content component-catalog bom-overview" aria-labelledby="bom-overview-title" aria-busy={loading}>
      <div className="card-head table-head stamm-content-head">
        <div className="stamm-title-row"><h2 id="bom-overview-title"><Trans>Stücklisten</Trans></h2></div>
      </div>
      <div className="table-toolbar">
        <input type="search" value={search} placeholder={t`Produkt, Version oder Komponente suchen…`}
          aria-label={t`Stücklisten durchsuchen`} onChange={(event) => changeSearch(event.target.value)} />
        <label className="check">
          <input type="checkbox" checked={kevOnly} onChange={(event) => { clearFocus(); setKevOnly(event.target.checked); }} />
          <Trans>Nur mit KEV-Treffern</Trans>
        </label>
        {data ? <div className="table-toolbar-options">
          <span className="table-count" aria-live="polite">
            <Plural value={visibleGroups.filter((group) => group.productId !== null).length} one="# Produkt" other="# Produkte" />
            {' · '}<Plural value={visibleVersions} one="# Version" other="# Versionen" />
          </span>
        </div> : null}
      </div>
      {error ? <Banner kind="error" sub={data ? t`Die angezeigten Daten konnten nicht aktualisiert werden.` : undefined}>{error}</Banner> : null}
      {loading && !data ? <PageSpinner text={t`Lädt Stücklisten…`} /> : data && visibleGroups.length === 0 ? (
        <EmptyState title={search || kevOnly ? t`Keine passenden Stücklisten` : t`Noch keine Produktversionen`}
          action={search || kevOnly ? <button type="button" onClick={() => { changeSearch(''); setKevOnly(false); }}><Trans>Filter zurücksetzen</Trans></button> : undefined}>
          {search || kevOnly ? t`Passen Sie die Suche oder den Filter an.` : t`Legen Sie unter „Produkte“ eine Version an.`}
        </EmptyState>
      ) : data ? <div className="component-table"><DataTable rows={visibleGroups} columns={columns} rowKey={(group) => group.key}
        renderDetail={renderDetail} expandedKeys={expanded} onToggleRow={(key) => {
          clearFocus(); setExpanded((current) => { const next = new Set(current); if (next.has(key)) next.delete(key); else next.add(key); return next; });
        }} pageSize={15} paginationResetKey={`${search}:${kevOnly}`} revealRowKey={focusKey} mobileBreakpoint={760}
        caption={t`Stücklisten je Produkt und Version`} rowClassName={(group) => group.key === focusKey ? 'stamm-focus' : undefined} /></div> : null}

      {editRow ? <BomEditor row={editRow} rows={rows} onClose={() => setEditing(null)} onSaved={onSaved} /> : null}
    </section>
  );
}
